// Accès Supabase Storage pour le bucket PRIVÉ `preuves-abonnements`.
//
// Convention de chemin : `<dossier-unique>/<nom-fichier-nettoye>`.
// Le bucket est privé (RLS Storage) : la lecture se fait exclusivement via une
// URL signée. Le Super Admin voit toutes les preuves ; un client ne voit que
// celles rattachées à son propre abonnement (policies `preuves_storage_*`).

import { supabase, getSafeUser } from "./supabase";
import { sanitizeFileName } from "./storage";
import {
  PREUVE_MAX_SIZE,
  PREUVE_MIME_TYPES,
  PREUVES_BUCKET,
  PREUVE_EXTENSIONS,
} from "@/config/paiement";

/** Valide un fichier de preuve. Retourne un message d'erreur, ou null si valide. */
export function validatePreuveFile(file: File): string | null {
  if (!PREUVE_MIME_TYPES.includes(file.type)) {
    return `Format non pris en charge. Formats acceptés : ${PREUVE_EXTENSIONS}.`;
  }
  if (file.size > PREUVE_MAX_SIZE) {
    return "Fichier trop volumineux (5 Mo maximum).";
  }
  return null;
}

/** Construit un chemin Storage unique, placé dans le dossier de l'utilisateur connecté. */
export async function buildPreuvePath(fileName: string): Promise<string> {
  const user = await getSafeUser();
  const uid = user?.id ?? "divers";
  const unique = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return `${uid}/${unique}-${sanitizeFileName(fileName)}`;
}

/** Téléverse la preuve et retourne le chemin Storage (valeur de `preuve_url`). */
export async function uploadPreuve(file: File): Promise<string> {
  const path = await buildPreuvePath(file.name || "preuve");
  const { error } = await supabase.storage.from(PREUVES_BUCKET).upload(path, file, {
    upsert: false,
    contentType: file.type || "application/octet-stream",
  });
  if (error) throw error;
  return path;
}

/** Génère une URL signée (bucket privé) pour consulter une preuve. */
export async function createSignedPreuveUrl(path: string, expiresIn = 3600): Promise<string> {
  if (!path) return "";
  const { data, error } = await supabase.storage.from(PREUVES_BUCKET).createSignedUrl(path, expiresIn);
  if (error) {
    console.error("[Preuves] URL signée impossible", path, error.message);
    return "";
  }
  return data?.signedUrl ?? "";
}

/** Supprime une preuve (best effort : ne lève jamais). */
export async function removePreuve(path: string): Promise<void> {
  if (!path) return;
  const { error } = await supabase.storage.from(PREUVES_BUCKET).remove([path]);
  if (error) console.error("[Preuves] suppression impossible", path, error.message);
}